import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, ChevronDown, AlertTriangle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Section, SectionHead } from "@/components/common";
import { formatPrice, feeBadge, AVAILABILITY } from "@/lib/ads";

function PackageCard({ p }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const avail = AVAILABILITY[p.availability] || AVAILABILITY.available;
  const soldOut = p.availability === "sold_out";

  return (
    <div data-testid={`package-card-${p.id}`} className="card p-6 flex flex-col">
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-display font-bold text-xl text-white">{p.name}</h3>
        <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full ${avail.cls}`}>{avail.label}</span>
      </div>
      <p className="text-slate-400 text-sm mt-1.5 min-h-[40px]">{p.summary}</p>

      <div className="mt-4 flex items-end gap-2 flex-wrap">
        <span className="font-display font-black text-3xl text-white">{formatPrice(p)}</span>
        <span className={`mb-1 font-mono text-[10px] px-2 py-0.5 rounded-full ${p.fee_type === "one_time" ? "bg-purple-500/20 text-purple-300" : "bg-blue-500/20 text-blue-300"}`}>{feeBadge(p)}</span>
      </div>
      {p.is_placeholder && (
        <span data-testid={`placeholder-badge-${p.id}`} className="mt-2 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-amber-400 bg-amber-500/10 border border-amber-500/30 px-2 py-1 rounded w-fit">
          <AlertTriangle size={11} /> Placeholder price
        </span>
      )}
      {p.duration && <p className="text-slate-500 text-xs mt-2">{p.duration}</p>}

      <div className="mt-4 space-y-1.5">
        {(p.included || []).map((inc, i) => (
          <div key={i} className="flex items-start gap-2 text-sm text-slate-300"><Check size={15} className="text-[#84CC16] mt-0.5 shrink-0" /> {inc}</div>
        ))}
        {(p.not_included || []).map((ni, i) => (
          <div key={i} className="flex items-start gap-2 text-sm text-slate-500"><X size={15} className="text-slate-600 mt-0.5 shrink-0" /> {ni}</div>
        ))}
      </div>

      <button data-testid={`toggle-terms-${p.id}`} onClick={() => setOpen(!open)}
        className="mt-4 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white">
        Requirements &amp; terms <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 space-y-2 text-xs text-slate-400 border-t border-white/10 pt-3">
          {p.requirements && <p><span className="text-slate-300 font-semibold">Requirements: </span>{p.requirements}</p>}
          {p.renewal_terms && <p><span className="text-slate-300 font-semibold">Renewal: </span>{p.renewal_terms}</p>}
          {p.cancellation_terms && <p><span className="text-slate-300 font-semibold">Cancellation: </span>{p.cancellation_terms}</p>}
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-white/10">
        <button data-testid={`select-package-${p.id}`} disabled={soldOut}
          onClick={() => navigate(`/advertise/signup?package=${p.id}`)}
          className={`w-full font-bold py-3 rounded-lg transition-colors ${soldOut
            ? "bg-slate-800 text-slate-500 cursor-not-allowed"
            : p.checkout_enabled ? "bg-[#84CC16] hover:bg-[#9ee325] text-[#0B0F17]" : "bg-[#2563EB] hover:bg-[#3B82F6] text-white"}`}>
          {soldOut ? "Sold out" : p.checkout_enabled ? "Sign up & checkout" : "Request this placement"}
        </button>
        {!p.checkout_enabled && !soldOut && <p className="text-[11px] text-slate-500 mt-2 text-center">No payment collected — we confirm details first</p>}
      </div>
    </div>
  );
}

export default function AdvertisePricing() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/ad-packages").then((r) => setPackages(r.data));
    api.get("/config").then((r) => setCfg(r.data));
  }, []);

  const placements = packages.filter((p) => p.category === "placement");
  const partners = packages.filter((p) => p.category === "launch_partner");

  return (
    <Section className="py-12 sm:py-16">
      <SectionHead eyebrow="Pricing &amp; packages" title="Clear prices. Clear terms."
        desc="Fixed pricing for each placement and launch-partner option. One-time and recurring fees are labeled. Payment is never required to submit a request." />

      {cfg?.has_placeholder_prices && (
        <div data-testid="placeholder-banner" className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-4 mb-6 flex items-start gap-3">
          <AlertTriangle size={18} className="text-amber-400 mt-0.5 shrink-0" />
          <p className="text-amber-200/90 text-sm">The prices below are <strong>editable placeholders</strong> set by the site owner and are not final. Checkout is disabled until final prices are entered in the admin area.</p>
        </div>
      )}
      {cfg && !cfg.payments_enabled && (
        <p className="text-slate-400 text-sm mb-6" data-testid="checkout-status">
          Online checkout is not yet connected. Submitting selects "<strong className="text-white">Request this placement</strong>" and emails your details to our team — no payment is taken.
        </p>
      )}

      <h2 className="font-display font-bold text-2xl text-white mb-4">Advertising placements</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
        {placements.map((p) => <PackageCard key={p.id} p={p} />)}
      </div>

      <h2 className="font-display font-bold text-2xl text-white mb-2">Launch partners</h2>
      <p className="text-slate-400 text-sm mb-4 max-w-2xl">Bundled, higher-visibility packages for companies that want to grow alongside DYORUSA. All placements remain clearly labeled and never influence editorial content.</p>
      <div className="grid sm:grid-cols-2 gap-5">
        {partners.map((p) => <PackageCard key={p.id} p={p} />)}
      </div>

      <div className="mt-12 text-center">
        <button data-testid="pricing-cta-request" onClick={() => navigate("/advertise/signup")}
          className="inline-flex items-center gap-2 border border-white/15 hover:border-blue-500/60 text-white font-semibold px-6 py-3 rounded-full transition-colors">
          Not sure which? Start a request <ArrowRight size={16} />
        </button>
      </div>
    </Section>
  );
}
